/**
 * Address Page V2 Controller
 *
 * Follows the Controller/View pattern used in the codebase.
 * Controller handles: hooks, data derivation, handlers with analytics, modal orchestration.
 * View handles: pure render, JSX composition.
 *
 * NO JSX in Controller - only data and handlers.
 */

import { useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { getTenantById, getThemeByTenantId, getCurrencyForCountry } from '@tenants';
import { useModalContext, CONNECTION_REQUEST_POPUP_MODAL } from 'contexts/modal';
import { AnalyticsController } from 'analytics/analytics-controller';
import { ConnectionRequestAnalytics } from 'analytics/connection-request-analytics';
import {
  GTM_CLICK_ADDRESS_GALLERY_SEE_ALL,
  GTM_CLICK_ADDRESS_HERO_IMAGE_SWIPE,
  GTM_CLICK_LISTING_MODAL_ADDRESS_TEXT,
  GTM_CLICK_ADDRESS_PAGE_IMAGE,
  GTM_CLICK_ADDRESS_PAGE_PRICE_HISTORY_BUTTON,
  GTM_CLICK_STICKY_HEADER_TAB,
} from 'constants/events';
import type { CurrencyCode } from 'types/currencies';
import { ListingAccessPolicy } from 'domain/listing/policies';
import { ListingSectionId } from 'domain/listing/types/listing-sections-config';
import { useVirtualTourButton } from 'hooks/use-virtual-tour-button';
import { useListingCompliance } from 'hooks/use-listing-compliance';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { ExperimentVariants } from 'utils/types';

import { useGallerySection, useHeaderSection } from 'sections';
import { useOpenHouse, type OpenHouseData } from 'ui/open-house';
import { useCompliance, type ComplianceData } from 'ui/compliance';
import { useExperimentVariation } from 'ui/experimental-section/hooks';
import { AllowedExperimentKey } from '@tenants/types';

import type { AddressPageCommonData, AddressPageEventHandlers } from '../shared';
import { FEATURE_FLAGS, hasFeatureFlag } from '../types';
import type { GallerySectionData } from 'sections/listing/gallery/types';
import type { HeaderSectionData } from 'sections/listing/header/types';
import type { ActionsSectionData } from 'sections/listing/actions/types';
import AddressPageV2View from './address-page-v2-view';
import { ComplianceRequirement } from 'types/compliance';
import { isComingSoon } from 'types/reso';

export interface AddressPageV2ControllerProps {
  data: AddressPageCommonData;
  handlers: AddressPageEventHandlers;
}

/**
 * Data passed from Controller to View
 * 
 * Only includes:
 * - Values derived from hooks (currency, locale, etc.)
 * - Section data from hooks
 * - Original commonData for View to extract listing data
 */
export interface AddressPageV2ViewData {
  // Refs
  topOfListingRef: React.RefObject<HTMLDivElement>;

  // Derived values (require hooks/tenant config)
  currency: CurrencyCode;
  locale: string;
  hasAccessRestriction: boolean;
  isBreadcrumbsSectionVisible: boolean;

  // MLS compliance data (requires hook)
  mlsCourtesyText: string | undefined;
  mlsBuyerRepText: string | undefined;

  // Virtual tour (requires hook)
  virtualTourLabel: string;
  onVirtualTourClick: () => void;

  // Price history (sticky tabs variation only)
  priceHistoryLabel?: string;
  priceHistoryRef: React.RefObject<HTMLDivElement>;
  onPriceHistoryClick: () => void;

  // Section data (from hooks)
  galleryData: GallerySectionData;
  headerData: HeaderSectionData;
  complianceData: ComplianceData;
  openHouseData: OpenHouseData;

  // Sticky footer data
  stickyFooterData: ActionsSectionData;

  // Original common data (View extracts listing data from here)
  commonData: AddressPageCommonData;
}

/**
 * Handlers passed from Controller to View
 */
export interface AddressPageV2ViewHandlers {
  onTermsOfUseBannerDismiss: (() => void) | undefined;
  onImageClick: ((index: number) => void) | undefined;
  onComplianceClick: (
    complianceRequirement?: ComplianceRequirement,
    providerId?: number
  ) => void;
  onSeeAllClick: () => void;
  onImageSwipe: () => void;
  onRequestInfo: (onFormSubmitted: () => void) => void;
  onBookShowing: (onFormSubmitted: () => void) => void;
  onAddressClick: () => void;
  onStickyHeaderTabClick: (tabId: ListingSectionId) => void;
  onSimilarListingsCardClick: AddressPageEventHandlers['onSimilarListingsCardClick'];
  onContactForPriceClick: AddressPageEventHandlers['onContactForPriceClick'];
  onViewListingClick: AddressPageEventHandlers['onViewListingClick'];
  onRoomsShowDetailsClick: AddressPageEventHandlers['onRoomsShowDetailsClick'];
}

/**
 * AddressPageV2Controller
 *
 * Handles all hooks, data derivation, and event handlers with analytics.
 * Renders the pure AddressPageV2View with data and handlers.
 *
 * NO JSX here - only data transformation and handler creation.
 */
export function AddressPageV2Controller({
  data: commonData,
  handlers: eventHandlers,
}: AddressPageV2ControllerProps) {
  const {
    tenantId,
    listing,
    isSaved,
    breadcrumbs,
    complianceRequirement,
    flags,
    soldListingExperimentId,
    history,
  } = commonData;

  const {
    onSwiperCarouselImageClick,
    onComplianceRequirementClick,
    onTermsOfUseBannerDismiss,
    onSaveListingClick,
    onSimilarListingsCardClick,
    onContactForPriceClick,
    onViewListingClick,
    onRoomsShowDetailsClick,
  } = eventHandlers;

  // Hooks
  const { t } = useTranslation(Namespaces.COMMON);
  const { openModal, closeAllModals } = useModalContext();
  const tenant = getTenantById(tenantId);
  const topOfListingRef = useRef<HTMLDivElement>(null);
  const priceHistoryRef = useRef<HTMLDivElement>(null);
  const virtualTourButton = useVirtualTourButton(false);
  const { locale } = useI18nFormatting();
  const theme = getThemeByTenantId(tenantId);

  // Currency from tenant config
  const currency = useMemo(
    () => getCurrencyForCountry(
      listing.countryCode,
      tenant.localization.currencyMap,
      tenant.localization.defaultCountry
    ),
    [listing.countryCode, tenant.localization.currencyMap, tenant.localization.defaultCountry]
  );

  // Access policy
  const accessPolicy = useMemo(
    () => ListingAccessPolicy.from(complianceRequirement),
    [complianceRequirement]
  );
  const { hasAccessRestriction } = accessPolicy;

  // Experiment variation for connection request form (used to fire Xp-namespaced CTA events)
  const { variationId: connectionRequestFormVariationId } = useExperimentVariation(
    AllowedExperimentKey.CONNECTION_REQUEST_FORM
  );

  // Section data hooks
  const galleryData = useGallerySection(listing, undefined); // View will add badges
  const complianceData = useCompliance(complianceRequirement);
  const openHouseData = useOpenHouse(listing.openHouses);

  // MLS compliance data
  const { data: mlsData } = useListingCompliance({
    providerId: listing.providerId,
    tenantThemeId: theme.id,
    listingId: listing.id,
    brokerage: listing.brokerage,
    brokerageEmail: listing.brokerageEmail,
    brokeragePhone: listing.brokeragePhone,
    lastFetched: listing.lastFetched,
    listingAgent: listing.listingAgent,
    listingAgentContact: listing.listingAgentContact,
    originalMlsName: listing.originalMlsName,
    province: listing.province,
    standardStatus: listing.standardStatus,
    buyerInfo: listing.buyerInfo ?? undefined,
  });

  // Share data
  const shareText = useMemo(() => {
    const { seoStreet, soldPrice, price, isRental, bedrooms, bathrooms } =
      listing;
    return [
      t('share_property_intro', {
        tenantName: tenant.theme.name,
        defaultValue: `Check out this property on ${tenant.theme.name}:`,
      }),
      seoStreet,
      `${soldPrice || price}` + (isRental ? '/month' : ''),
      `${bedrooms} beds, ${bathrooms} baths`,
    ].join('\n');
  }, [listing, tenant.theme.name, t]);
  const shareUrl = `${tenant.hosts[0] + listing.addressPathWithFallback}`;

  // Header section data
  const headerData = useHeaderSection({
    breadcrumbs,
    isSaved,
    shareUrl,
    shareText,
    onLikeToggle: () => onSaveListingClick?.(listing.id),
  });

  // Visibility flags
  const isBreadcrumbsSectionVisible = hasFeatureFlag(
    flags,
    FEATURE_FLAGS.BREADCRUMBS_ENABLED
  );

  // Event handlers with analytics
  const onSeeAllClick = useCallback(() => {
    AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_GALLERY_SEE_ALL);
    // Open the gallery modal at the first image
    onSwiperCarouselImageClick?.(0);
  }, [onSwiperCarouselImageClick]);

  const onImageSwipe = useCallback(() => {
    AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_HERO_IMAGE_SWIPE);
  }, []);

  const onComplianceClick = useCallback((
    overrideRequirement?: ComplianceRequirement,
    overrideProviderId?: number
  ) => {
    onComplianceRequirementClick?.(
      overrideRequirement ?? complianceRequirement,
      overrideProviderId ?? listing.providerId
    );
  }, [onComplianceRequirementClick, complianceRequirement, listing.providerId]);

  const onAddressClick = useCallback(() => {
    AnalyticsController.trackEvent(GTM_CLICK_LISTING_MODAL_ADDRESS_TEXT);
    closeAllModals();
  }, [closeAllModals]);

  const onStickyHeaderTabClick = useCallback((tabId: ListingSectionId) => {
    // TODO: move this to a dedicated contextual analytics API once standardized.
    AnalyticsController.trackEvent(
      GTM_CLICK_STICKY_HEADER_TAB,
      'address-page',
      tabId
    );
  }, []);

  const openPVRModal = useCallback(
    (isViewingRequest: boolean, buttonText: string, onFormSubmitted: () => void) => {
      // Track sold listing experiment event if applicable
      if (soldListingExperimentId === ExperimentVariants.A) {
        AnalyticsController.trackEvent('XpA_AddressTakeATourButClk');
      }
      ConnectionRequestAnalytics.onCtaClick(buttonText, connectionRequestFormVariationId);
      openModal(CONNECTION_REQUEST_POPUP_MODAL, {
        listing,
        isViewingRequest,
        prefillData: {},
        onFormSubmitted,
      });
    },
    [listing, openModal, soldListingExperimentId, connectionRequestFormVariationId]
  );

  const onRequestInfo = useCallback((onFormSubmitted: () => void) => {
    openPVRModal(
      false,
      t(LOCALE_COMMON_PATHS.LISTING_STICKY_FOOTER_REQUEST_INFO, { defaultValue: 'Request More Info' }),
      onFormSubmitted,
    );
  }, [openPVRModal, t]);

  const onBookShowing = useCallback((onFormSubmitted: () => void) => {
    openPVRModal(
      true,
      t(LOCALE_COMMON_PATHS.LISTING_STICKY_FOOTER_BOOK_SHOWING, { defaultValue: 'Book a Showing' }),
      onFormSubmitted,
    );
  }, [openPVRModal, t]);

  const onPriceHistoryClick = useCallback(() => {
    if (priceHistoryRef.current) {
      priceHistoryRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_PRICE_HISTORY_BUTTON);
  }, []);

  // Price history button (only show if price history is enabled and has more than current listing)
  const showPriceHistoryButton = 
    hasFeatureFlag(flags, FEATURE_FLAGS.PRICE_HISTORY_ENABLED) && 
    history.length > 1;

  const priceHistoryLabel = showPriceHistoryButton
    ? t(LOCALE_COMMON_PATHS.LISTING_CONTENT_PRICE_HISTORY_BUTTON_LABEL, { defaultValue: 'Price History' })
    : undefined;

  // Sticky footer data
  const isComingSoonListing = !!listing.standardStatus && isComingSoon(listing.standardStatus);
  const stickyFooterData: ActionsSectionData = {
    primaryAction: { label: '', onClick: () => onRequestInfo(() => {}) },
    secondaryAction: { label: '', onClick: () => onBookShowing(() => {}) },
    isComingSoon: isComingSoonListing,
    share: { text: shareText, url: shareUrl },
    favorite: {
      isFavorited: isSaved,
      onToggle: () => onSaveListingClick?.(listing.id),
    },
  };

  // Assemble data for View
  const viewData: AddressPageV2ViewData = {
    topOfListingRef,
    currency,
    locale,
    hasAccessRestriction,
    isBreadcrumbsSectionVisible,
    mlsCourtesyText: mlsData?.courtesyText,
    mlsBuyerRepText: mlsData?.buyerRepText,
    virtualTourLabel: virtualTourButton.label,
    onVirtualTourClick: virtualTourButton.onClick,
    priceHistoryLabel,
    priceHistoryRef,
    onPriceHistoryClick,
    galleryData,
    headerData,
    complianceData,
    openHouseData,
    stickyFooterData,
    commonData,
  };

  // Wrap the legacy handler with analytics for direct image clicks
  const onImageClick = onSwiperCarouselImageClick
    ? (index: number) => {
      AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_IMAGE);
      onSwiperCarouselImageClick(index);
    }
    : undefined;

  // Assemble handlers for View
  const viewHandlers: AddressPageV2ViewHandlers = {
    onTermsOfUseBannerDismiss,
    onImageClick,
    onComplianceClick,
    onSeeAllClick,
    onImageSwipe,
    onRequestInfo,
    onBookShowing,
    onAddressClick,
    onStickyHeaderTabClick,
    onSimilarListingsCardClick,
    onContactForPriceClick,
    onViewListingClick,
    onRoomsShowDetailsClick,
  };

  return <AddressPageV2View data={viewData} handlers={viewHandlers} />;
}

export default AddressPageV2Controller;
